import {
  action,
  makeObservable,
  observable,
  ObservableMap,
  runInAction,
} from 'mobx';
import {
  IGetUserResponse,
  IRecipient,
  IRecipientForOmniLogin,
  IUser,
} from '../interfaces/user.interface';
import RootStore from './RootStore';
import {
  ILoginRequestParameter,
  IOmniLoginRequestParameter,
} from '../interfaces/login.interface';
import axios from 'axios';
import { IInquiryTarget } from '../interfaces/inquiry.interface';
import { LocalStorageKeyEnum } from '../utils/local-storage-handler';

export default class UserStore {
  private rootStore: RootStore;

  @observable user: IUser = observable.object({
    serviceId: 0,
    serviceAttribute: 0,
    userId: '',
    userIcon: '',
    userName: '',
  });
  @observable recipients: ObservableMap<string, IRecipient> = observable.map();
  // the token is re-used at Socket query in SocketStore
  @observable token: string = '';
  @observable inquiryTarget: IInquiryTarget = {
    categoryId: -1,
    targetId: '',
    targetName: '',
  };

  @action
  fetchUserData = async (res: IGetUserResponse) => {
    runInAction(() => {
      this.user = { ...res.user } as IUser;
      res.recipients.forEach((recipient) => {
        this.recipients.set(recipient.userId, recipient);
      });
      this.inquiryTarget = res.inquiryTarget;
    });
  };

  @action
  generateLoginParams = (
    searchParams: URLSearchParams,
    destinationAfterLogin: string,
  ): ILoginRequestParameter => {
    return {
      serviceId: Number(searchParams.get('serviceId')),
      serviceAttribute: Number(searchParams.get('serviceAttribute')),
      recipients: [
        {
          serviceId: Number(searchParams.get('serviceId')),
          serviceAttribute: Number(searchParams.get('serviceAttribute')),
          authId: searchParams.get('recipientId')!,
        },
      ],
      categoryId: Number(searchParams.get('categoryId')),
      targetId: searchParams.has('targetId')
        ? decodeURIComponent(searchParams.get('targetId') as string)
        : '',
      targetName: searchParams.has('targetName')
        ? decodeURIComponent(searchParams.get('targetName') as string)
        : '',
      targetUrl: searchParams.has('targetUrl')
        ? decodeURIComponent(searchParams.get('targetUrl') as string)
        : '',
      destination: destinationAfterLogin,
    };
  };

  @action
  getLoginParamsFromEncodedState =
    async (): Promise<IOmniLoginRequestParameter> => {
      const { code, decodedState } =
        await this.rootStore.omniLoginStore.extractOmniStateAndDecodeState();

      if (decodedState.serviceId && decodedState.serviceAttribute) {
        return {
          serviceId: Number(decodedState.serviceId),
          serviceAttribute: Number(decodedState.serviceAttribute),
          recipients: decodedState.recipients.map(
            (recipient: IRecipientForOmniLogin) => {
              return {
                serviceId: Number(recipient.serviceId),
                serviceAttribute: Number(recipient.serviceAttribute),
                authId: recipient.authId,
              };
            },
          ),
          categoryId: Number(decodedState.categoryId),
          targetId: decodedState.targetId,
          targetName: decodedState.targetName,
          targetUrl: decodedState.targetUrl,
          code: code,
          destination: decodedState.destination,
        };
      } else {
        console.warn('state is invalid. set /inquiry for destination');
        return {
          code: code,
          serviceId: this.user.serviceId,
          serviceAttribute: this.user.serviceAttribute,
          // for inquiry list page, no recipients, no categoryId, no targetId are needed
          recipients: [],
          categoryId: -1, // TODO: magic number?
          targetId: '',
          targetName: '',
          targetUrl: '',
          destination: '/inquiry',
        };
      }
    };

  @action
  getUserName = (userId: string) => {
    if (this.user.userId === userId) {
      return this.user.userName;
    }
    return this.recipients.get(userId)
      ? this.recipients.get(userId)!.userName
      : '';
  };

  @action
  setTheLatestAuthToken = () => {
    runInAction(() => {
      const localToken = localStorage.getItem(LocalStorageKeyEnum.TOKEN)!;
      axios.defaults.headers.common['Authorization'] = `Bearer ${localToken}`;
      this.token = localToken;
    });
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }
}
